import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import NavigationHeader from "../../components/ui/NavigationHeader";
import MainNavigation from "../../components/ui/MainNavigation";
import BreadcrumbNavigation from "../../components/ui/BreadcrumbNavigation";
import QuickActionPanel from "../../components/ui/QuickActionPanel";
import MetricsCard from "./components/MetricsCard";
import ActivityFeed from "./components/ActivityFeed";
import SalesChart from "./components/SalesChart";
import TopProductsWidget from "./components/TopProductsWidget";
import AlertsPanel from "./components/AlertsPanel";
import QuickStatsGrid from "./components/QuickStatsGrid";
import Icon from "../../components/AppIcon";
import { useDispatch, useSelector } from "react-redux";
import { getNotificationsService, getStatisticsService } from "redux/services/general_service";

const Dashboard = () => {
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const { statistics } = useSelector((state) => state.general);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const [user, setUser] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("Token");
    if (!token) {
      navigate("/login");
      return;
    }

    setUser({
      name: "Admin User",
      email: "admin@crm.com",
      role: "Administrator",
    });

    dispatch(getStatisticsService());
    dispatch(getNotificationsService());
  }, [navigate]);

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const mainMetrics = [
    {
      title: t("totalCustomers"),
      value: statistics?.all_clients_count,
      change: "+12.5%",
      changeType: "positive",
      icon: "Users",
      color: "primary",
    },
    {
      title: t("totalProducts"),
      value: statistics?.products_count,
      change: "+8.2%",
      changeType: "positive",
      icon: "Package",
      color: "accent",
    },
    {
      title: t("categories"),
      value: statistics?.all_categories_count,
      change: "+2.1%",
      changeType: "positive",
      icon: "Tags",
      color: "success",
    },
    {
      title: t("recentOrders"),
      value: statistics?.new_orders_count,
      change: "-3.4%",
      changeType: "negative",
      icon: "ShoppingCart",
      color: "warning",
    },
  ];

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <Icon
            name="Loader2"
            size={24}
            className="animate-spin text-primary"
          />
          <span className="text-foreground">{t("loading")}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <NavigationHeader
        user={user}
        onMenuToggle={handleSidebarToggle}
        sidebarCollapsed={sidebarCollapsed}
      />
      <MainNavigation
        isCollapsed={sidebarCollapsed}
        onToggle={handleSidebarToggle}
      />
      <main
        className={`transition-layout pt-16 ${
          sidebarCollapsed ? "lg:pl-16" : "lg:pl-60"
        }`}
      >
        <BreadcrumbNavigation />
        <div className="p-6 space-y-8">
          <div className="bg-gradient-to-r from-primary to-accent rounded-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold mb-2">
                  {t("welcome", { name: user.name })}
                </h1>
                <p className="text-white/90">
                  {t("Here's what's happening with your business today.")}
                </p>
              </div>
              <div className="hidden md:block">
                <Icon name="BarChart3" size={48} className="text-white/20" />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {mainMetrics.map((metric, index) => (
              <MetricsCard
                key={index}
                title={metric.title}
                value={metric.value}
                change={metric.change}
                changeType={metric.changeType}
                icon={metric.icon}
                color={metric.color}
              />
            ))}
          </div>
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="xl:col-span-2">
              <SalesChart />
            </div>
            <div className="xl:col-span-1">
              <TopProductsWidget />
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <AlertsPanel />
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
